import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Cylinder } from "@react-three/drei";
import { forwardRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface CylinderMeshProperties extends TransformableItemProperties {
  radiusTop: number;
  radiusBottom: number;
  height: number;
  radialSegments: number;
  heightSegments: number;
  openEnded: boolean;
  materialId?: string;
}

export interface CylinderMesh extends Mesh<CylinderMeshProperties> {
  subtype: 'cylinder';
  properties: CylinderMeshProperties;
}

// Create a reusable Cylinder component that can be used in both preview and world
const CylinderComponent = forwardRef<THREE.Mesh, { properties: CylinderMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { radiusTop, radiusBottom, height, radialSegments, heightSegments, openEnded, transform, materialId } = properties;
  const { activeWorld } = useAppContext();

  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Cylinder
      ref={ref}
      args={[radiusTop, radiusBottom, height, radialSegments, heightSegments, openEnded]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const CylinderMeshItem: CylinderMesh = {
  id: '',
  name: 'Cylinder',
  type: 'mesh',
  subtype: 'cylinder',
  icon: 'Cylinder',
  properties: {
    radiusTop: 1,
    radiusBottom: 1,
    height: 2,
    radialSegments: 32,
    heightSegments: 1,
    openEnded: false,
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.radiusTop',
      type: 'number',
      label: 'Top Radius',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.radiusBottom',
      type: 'number',
      label: 'Bottom Radius',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.height',
      type: 'number',
      label: 'Height',
      default: 2,
      panel: 'geometry'
    },
    {
      key: 'properties.radialSegments',
      type: 'number',
      label: 'Radial Segments',
      default: 32,
      panel: 'geometry'
    },
    {
      key: 'properties.heightSegments',
      type: 'number',
      label: 'Height Segments',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.openEnded',
      type: 'boolean',
      label: 'Open Ended',
      default: false,
      panel: 'geometry'
    }
  ],
  render: (properties: CylinderMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <CylinderComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 